/* Shared size-step presentation. Builders own controls, state, ordering and events. */
(function (scope) {
  'use strict';

  scope.theme = scope.theme || {};
  if (scope.theme.sizeStep) return;

  var V = 64, FIT = 60, STROKE = 1.6, GAP = 3, DOT = 0.68;
  var ASPECT = { v: 1 / 1.6, vr: 1 / 1.6, pair: 1 / 1.7, trio: 1 / 2.6, quad: 1 / 3.4, stack: 1.25 };
  var POCKETS = { pair: 2, trio: 3, quad: 4, stack: 2 };
  var reported = Object.create(null);
  var ROW_CLASS = 'tw:relative tw:box-border tw:flex tw:min-h-[60px] tw:w-full tw:flex-row tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-1 tw:rounded-xl tw:py-2 tw:pr-3 tw:pl-2 tw:text-left tw:max-[399px]:overflow-visible';

  function element(tag, classes, text) {
    var node = scope.document.createElement(tag);
    node.className = classes || '';
    if (text != null) node.textContent = text;
    return node;
  }

  function addClasses(node, classes) {
    classes.split(' ').forEach(function (name) { if (name) node.classList.add(name); });
  }

  function report(key, reason) {
    var id = String(key || '(empty list)') + ':' + reason;
    if (reported[id]) return;
    reported[id] = true;
    scope.console.error('Size step: "' + String(key || '(empty list)') + '" ' + reason + '; using photos for this list.');
  }

  function validCounts(counts) {
    return !!counts && Number.isInteger(counts.small) && counts.small >= 1
      && Number.isInteger(counts.fish_oil) && counts.fish_oil >= 0;
  }

  function shapeFor(key) {
    key = String(key || '');
    if (key.indexOf('nano-') === 0) return 'vr';
    if (key.indexOf('weekly-am-pm-') === 0 || key.indexOf('2-week-') === 0) return 'pair';
    return 'v';
  }

  // Pocket geometry uses a fixed 64-unit view box and one shared pitch per list.
  function pockets(shape) { return POCKETS[shape] || 1; }
  function layout(n, shape) {
    var target = ASPECT[shape], best = null;
    for (var cols = 1; cols <= n; cols++) {
      var rows = Math.ceil(n / cols), empty = cols * rows - n;
      if (empty >= cols) continue;
      var score = Math.abs(Math.log((cols / rows) / target)) + 0.25 * empty;
      if (!best || score < best.score) best = { cols: cols, rows: rows, score: score };
    }
    var pad = shape === 'vr' ? 0.5 : 0.22;
    return { cols: best.cols, rows: best.rows, pad: pad, w: best.cols + 2 * pad, h: best.rows + 2 * pad };
  }
  function pitchFor(items) {
    var p = Infinity;
    items.forEach(function (it) {
      var u = layout(it.count, it.shape), k = pockets(it.shape);
      if (it.shape === 'stack') p = Math.min(p, (FIT - STROKE) / u.w, (FIT - k * STROKE - (k - 1) * GAP) / (k * u.h));
      else p = Math.min(p, (FIT - k * STROKE - (k - 1) * GAP) / (k * u.w), (FIT - STROKE) / u.h);
    });
    return p;
  }
  function f(n) { return n.toFixed(2); }
  function iconSvg(shape, count, p, empty) {
    var u = layout(count, shape), r = p * DOT / 2, pw = u.w * p + STROKE, ph = u.h * p + STROKE;
    var n = pockets(shape), st = shape === 'stack', total = n * (st ? ph : pw) + (n - 1) * GAP;
    var x0 = st ? (V - pw) / 2 : (V - total) / 2, y0 = st ? (V - total) / 2 : (V - ph) / 2, out = '';
    for (var k = 0; k < n; k++) {
      var x = st ? x0 : x0 + k * (pw + GAP), y = st ? y0 + k * (ph + GAP) : y0;
      var rx = shape === 'vr' ? pw * 0.36 : Math.min(p * 0.9, pw * 0.25);
      out += '<rect x="' + f(x) + '" y="' + f(y) + '" width="' + f(pw) + '" height="' + f(ph) + '" rx="' + f(rx) + '" fill="#fff" stroke="currentColor" stroke-width="' + STROKE + '"' + (empty ? ' stroke-dasharray="3 2.2"' : '') + '/>';
      if (empty) continue;
      var ix = x + STROKE / 2 + u.pad * p, iy = y + STROKE / 2 + u.pad * p;
      for (var j = 0; j < u.rows; j++) {
        var inRow = Math.min(u.cols, count - j * u.cols), off = (u.cols - inRow) * p / 2;
        for (var i = 0; i < inRow; i++) out += '<circle cx="' + f(ix + off + p * (i + 0.5)) + '" cy="' + f(iy + p * (j + 0.5)) + '" r="' + f(r) + '" fill="currentColor"/>';
      }
    }
    return '<svg class="tw:block tw:h-full tw:w-full" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">' + out + '</svg>';
  }

  function capacity(counts, launch) {
    if (launch) return 'Pill counts at launch';
    if (!validCounts(counts)) return '';
    return counts.small + ' small pills' + (counts.fish_oil
      ? ' or ' + counts.fish_oil + (counts.fish_oil === 1 ? ' fish oil' : ' fish oils') : '');
  }

  // Prepare every icon before touching the DOM, so a drawing failure affects the whole list.
  // Row models contain { key, name, thumbnail, shape, counts, launch? }; payloads stay untouched.
  function prepare(items) {
    items = Array.isArray(items) ? items : [];
    var rows = items.map(function (item) { return Object.assign({}, item, { svg: '', capacity: '' }); });
    var invalid = rows.filter(function (item) { return !validCounts(item.counts) || !Object.prototype.hasOwnProperty.call(ASPECT, item.shape); });
    if (!rows.length) report('', 'has no drawable sizes');
    invalid.forEach(function (item) { report(item.key, 'has missing or invalid pill counts or pocket shape'); });
    if (!rows.length || invalid.length) return { icons: false, pitch: null, rows: rows };
    try {
      var pitch = pitchFor(rows.map(function (item) { return { shape: item.shape, count: item.counts.small }; }));
      if (!Number.isFinite(pitch) || pitch <= 0) throw new Error('Invalid pill pitch');
      rows.forEach(function (item) {
        item.svg = iconSvg(item.shape, item.counts.small, pitch, !!item.launch);
        item.capacity = capacity(item.counts, item.launch);
      });
      return { icons: true, pitch: pitch, rows: rows };
    } catch (error) {
      report(rows[0].key, 'could not draw pocket icons');
      rows.forEach(function (item) { item.svg = ''; item.capacity = ''; });
      return { icons: false, pitch: null, rows: rows };
    }
  }

  function fullName(prefix, displaySize, label) {
    var name = String(displaySize || '').trim();
    prefix = String(prefix || '').trim();
    if (!name) return String(label || '');
    var lower = name.toLowerCase(), start = prefix.toLowerCase();
    return !prefix || lower === start || lower.indexOf(start + ' ') === 0 ? name : prefix + ' ' + name;
  }

  function createTile(row, options) {
    options = options || {};
    var tile = element('span', 'tw:relative tw:box-border tw:block tw:shrink-0 tw:overflow-visible tw:rounded-lg tw:bg-[#f2f2f2] tw:p-[3px] tw:text-[#1a1a1a]');
    addClasses(tile, options.variant === 'option'
      ? 'tw:h-12 tw:w-12 tw:max-[374px]:h-10 tw:max-[374px]:w-10'
      : options.variant === 'trigger' ? 'tw:h-11 tw:w-11' : 'tw:h-[52px] tw:w-[52px]');
    tile.setAttribute('data-size-thumbnail-wrap', '');
    tile.setAttribute('aria-hidden', 'true');
    if (options.disabled) tile.classList.add('tw:opacity-60');
    if (row.svg) {
      tile.innerHTML = row.svg;
    } else if (row.thumbnail) {
      var image = element('img', 'tw:h-full tw:w-full tw:object-contain');
      image.src = row.thumbnail;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 128;
      image.height = 128;
      image.setAttribute('data-size-thumbnail', '');
      tile.appendChild(image);
    } else {
      var placeholder = element('span', 'tw:flex tw:h-full tw:items-center tw:justify-center tw:overflow-hidden tw:text-center tw:text-[9px] tw:font-semibold tw:text-gray-500', row.name);
      placeholder.setAttribute('data-size-thumbnail-placeholder', '');
      tile.appendChild(placeholder);
    }
    if (options.multiplier) {
      var chip = element('span', 'tw:absolute tw:-right-[5px] tw:-bottom-[5px] tw:rounded-[5px] tw:border-[1.5px] tw:border-white tw:bg-[#1a1a1a] tw:px-1 tw:py-[3px] tw:text-[10px] tw:leading-none tw:font-bold tw:text-white', '×' + options.multiplier);
      chip.setAttribute('aria-hidden', 'true');
      chip.setAttribute('data-size-multiplier', '');
      tile.appendChild(chip);
    }
    return tile;
  }

  function createText(row, variant) {
    var text = element('span', 'tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-px');
    var name = element('span', 'tw:min-w-0 tw:font-semibold tw:leading-[1.25] tw:text-gray-950');
    name.setAttribute('data-size-name', '');
    name.title = row.name || '';
    if (variant === 'trigger') {
      addClasses(name, 'tw:line-clamp-2 tw:text-sm');
    } else {
      name.classList.add('tw:block');
      addClasses(name, variant === 'option'
        ? 'tw:truncate tw:text-[13px] tw:max-[374px]:text-[12.5px]' : 'tw:truncate tw:text-sm');
    }
    var value = String(row.name || '');
    if (variant === 'option' && / Case$/.test(value)) {
      name.textContent = value.slice(0, -5);
      name.appendChild(element('span', 'tw:max-[374px]:hidden', ' Case'));
    } else name.textContent = value;
    text.appendChild(name);
    if (row.capacity && variant !== 'trigger') {
      var line = element('span', 'tw:block tw:truncate tw:text-xs tw:leading-[1.3] tw:text-[#444]', row.capacity);
      line.setAttribute('data-size-capacity', '');
      text.appendChild(line);
    }
    return text;
  }

  function setBadge(control, badge, text, tone, variant) {
    text = String(text || '');
    badge.className = 'tw:order-3 tw:shrink-0 tw:rounded-full tw:px-2 tw:py-0.5 tw:text-[10px] tw:font-semibold tw:leading-3 tw:whitespace-nowrap';
    addClasses(badge, tone === 'best'
      ? 'tw:bg-[#fef3c6] tw:text-[#973c08]'
      : tone === 'status-dark' ? 'tw:bg-[#6c6c6c] tw:text-white' : 'tw:bg-gray-200 tw:text-gray-700');
    badge.textContent = text;
    badge.classList.toggle('tw:hidden', !text);
    var long = text.length > 12;
    control.classList.toggle('tw:max-[399px]:pt-4', !!text && !long && !variant);
    if (long) {
      badge.classList.remove('tw:whitespace-nowrap');
      addClasses(badge, 'tw:order-5 tw:ml-16 tw:max-w-[calc(100%-64px)] tw:basis-full tw:text-left tw:whitespace-normal');
    } else if (!variant) {
      addClasses(badge, 'tw:max-[399px]:absolute tw:max-[399px]:top-1.5 tw:max-[399px]:right-2 tw:max-[399px]:z-2 tw:max-[399px]:rounded-md tw:max-[399px]:px-1.5');
    }
  }

  function createCheck(selected, variant) {
    var check = element('span', 'tw:order-4 tw:flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-full tw:bg-gray-950 tw:text-white');
    addClasses(check, variant === 'option' ? 'tw:h-4 tw:w-4'
      : 'tw:h-5 tw:w-5 tw:max-[399px]:absolute tw:max-[399px]:-top-1 tw:max-[399px]:-left-1 tw:max-[399px]:z-3 tw:max-[399px]:h-4 tw:max-[399px]:w-4 tw:max-[399px]:ring-2 tw:max-[399px]:ring-white');
    check.classList.toggle('tw:hidden', !selected);
    check.setAttribute('aria-hidden', 'true');
    check.setAttribute('data-size-check', '');
    check.innerHTML = '<svg class="tw:h-3 tw:w-3 tw:max-[399px]:h-2.5 tw:max-[399px]:w-2.5" viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 11.4 2.6 7.5 4 6.1l2.5 2.5L12 3.1l1.4 1.4z"/></svg>';
    return check;
  }

  function appendRow(control, row, options) {
    options = options || {};
    addClasses(control, ROW_CLASS);
    if (options.variant === 'option') {
      control.classList.remove('tw:py-2', 'tw:pr-3', 'tw:rounded-xl');
      addClasses(control, 'tw:py-1.5 tw:pr-2 tw:rounded-none');
    }
    control.appendChild(createTile(row, options));
    control.appendChild(createText(row, options.variant));
    var badge = element('span');
    badge.setAttribute('data-size-badge', '');
    setBadge(control, badge, options.badge, options.tone, options.variant);
    control.appendChild(badge);
    control.appendChild(createCheck(!!options.selected, options.variant));
    return control;
  }

  function appendOption(control, row, options) {
    return appendRow(control, row, Object.assign({}, options, { variant: 'option' }));
  }

  function appendTrigger(control, row, options) {
    options = options || {};
    var content = element('span', 'tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3');
    if (row) {
      content.appendChild(createTile(row, { variant: 'trigger', disabled: options.disabled }));
    } else {
      var placeholder = element('span', 'tw:box-border tw:flex tw:h-11 tw:w-11 tw:shrink-0 tw:items-center tw:justify-center tw:overflow-hidden tw:rounded tw:border tw:border-gray-200 tw:bg-gray-100 tw:text-gray-400');
      placeholder.setAttribute('data-size-thumbnail-wrap', '');
      placeholder.setAttribute('data-size-thumbnail-placeholder', '');
      placeholder.setAttribute('aria-hidden', 'true');
      placeholder.innerHTML = '<svg class="tw:h-5 tw:w-5" viewBox="0 0 24 24" fill="none"><rect x="5" y="3" width="14" height="18" rx="3" stroke="currentColor" stroke-width="1.5"/><path d="M9 8h6M9 12h6M9 16h4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
      content.appendChild(placeholder);
    }
    content.appendChild(createText(row || { name: options.placeholder || 'Select case size' }, 'trigger'));
    if (options.badge) {
      var badge = element('span');
      badge.setAttribute('data-size-badge', '');
      setBadge(content, badge, options.badge, options.tone, 'trigger');
      content.appendChild(badge);
    }
    control.appendChild(content);
    return control;
  }

  function createBar(pinned) {
    var bar = element('p', 'tw:flex tw:items-center tw:gap-1.5 tw:bg-mint tw:px-3 tw:py-[9px] tw:text-[13px] tw:leading-[1.3] tw:font-bold tw:text-black');
    addClasses(bar, pinned ? 'tw:m-0 tw:shrink-0 tw:rounded-none' : 'tw:mt-0 tw:mb-2.5 tw:rounded-[10px]');
    bar.setAttribute('data-size-capacity-bar', '');
    bar.innerHTML = '<svg class="tw:h-3.5 tw:w-3.5 tw:shrink-0" viewBox="0 0 14 14" aria-hidden="true" focusable="false"><rect x="2.5" y="1" width="9" height="12" rx="2.2" fill="none" stroke="currentColor" stroke-width="1.4"/><circle cx="5.5" cy="5" r="1" fill="currentColor"/><circle cx="8.5" cy="5" r="1" fill="currentColor"/><circle cx="5.5" cy="9" r="1" fill="currentColor"/><circle cx="8.5" cy="9" r="1" fill="currentColor"/></svg>';
    bar.appendChild(element('span', '', 'Pill counts are per pocket'));
    return bar;
  }

  scope.theme.sizeStep = {
    prepare: prepare,
    validCounts: validCounts,
    shapeFor: shapeFor,
    capacity: capacity,
    fullName: fullName,
    createTile: createTile,
    createText: createText,
    createCheck: createCheck,
    setBadge: setBadge,
    appendRow: appendRow,
    appendOption: appendOption,
    appendTrigger: appendTrigger,
    createBar: createBar,
    createPopoverHeader: function () { return createBar(true); }
  };
})(window);
